import React, {useEffect, useState} from "react";
import { Helmet } from "react-helmet-async";
import { LazyComponent } from "../../shared/lazy-image";
import PropTypes from "prop-types";
import styles from "./styles.css";
import loadable from "@loadable/component";
import { appUrl } from "../../../constants/url-constants";
import { sendCustomGaEvents } from "../../../tracking";
import { breadcrumbClicked } from "../../../constants/tracking-constants/breadcrumb";
const BreadcrumbList = loadable(() => import("../../shared/breadcrumb-list"), {ssr: true});
import SellUsedCarCityList from "../../shared/sell-used-car-city-list";
import ErrorHandler from "../../shared/with-error-handler";
import { popularCityTextClick, evaluateCarPriceClicked, evaluateCarPriceBottomClicked } from "./tracking";
import HomeBanner  from "../home-banner";
import Button from "../../shared/button";
import PilotFaq from "../pilot-faq";
import { NUMBER } from "../../../constants/app-constants";
import faqFormatter from "../../../utils/helpers/faq-formatter";
import useC2bCleverTap from "../../../hooks/use-c2b-clevertap";

const WhyCars = loadable(() => import("../why-cars"));
const SimplestWay = loadable(() => import("../simplest-way"));
const TestimonialCarouselRevamp = loadable(() => import("../testimonial-carousel-revamp"));

const UsedCarValuation = ({
    genericContent,
    data,
    mainHeading,
    subHeading,
    setIsSSRConnect,
    seo,
    isSSR,
    fetchDataConnect,
    list: testimonialList,
    fetchFAQConnect,
    faqList
}) => {
    useC2bCleverTap("Sell_car");
    const {
        content,
        imageContent,
        secondaryContent
    } = genericContent || {};
    const { top_cities: topCIties = [] } = data || {};
    const { default: seoDetails } = seo || {};
    const { title, description } = seoDetails || {};

    const [faqs, setFaqs] = useState(faqList || []);

    const onSubmit = (gaEvent) => {
        sendCustomGaEvents(gaEvent);
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth"
        });
    };

    const onBreadcrumbClick = ({ item }) => {
        if (item === "home") {
            sendCustomGaEvents({
                ...breadcrumbClicked,
                eventLabel: item
            });
            window.location.href = `${appUrl()}/`;
        } else {
            sendCustomGaEvents(breadcrumbClicked);
            window.location.href = `${appUrl()}${item.redirectURL}`;
        }
    };

    const computeBreadcrumbList = () => {
        const list = [{
            displayText: "Sell car",
            redirectURL: "/sell-used-cars/"
        },
        {
            displayText: `Used Car Valuation`,
            redirectURL: ``
        }];
        return list;
    };

    const handleCityListGa = (cityName) => {
        sendCustomGaEvents({
            ...popularCityTextClick,
            eventLabel: cityName
        });
    };

    useEffect(() => {
        setIsSSRConnect(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isSSR && !(faqList || []).length) {
            const faqPayload = {
                slug: "Used-car-valuation"
            };
            fetchFAQConnect(faqPayload)
                .then(response => {
                    const list = faqFormatter(response);
                    setFaqs(list);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [faqList]);

    useEffect(() => {
        if (!isSSR) {
            const cmsPayload = {
                cmsuri: "used-car-valuation",
                expand: "seo_content,customer_review_list,make_list",
                tag: "used-car-valuation"
            };
            fetchDataConnect(cmsPayload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSSR]);

    return (
        <div styleName="styles.pageWrapper">
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
            </Helmet>
            <HomeBanner
                mainHeading={mainHeading}
                subHeading={subHeading}
                ctaText={"evaluate car price"}
            />
            <div styleName={"styles.carValuationWrapper"}>
                <div styleName={"styles.contentWrapper"}  dangerouslySetInnerHTML={{ __html: content }} />
                <div styleName={"styles.rightValueWrapper"}>
                    <div styleName={"styles.rightValueText"}>
                        <div dangerouslySetInnerHTML={{ __html: imageContent }} />
                        <Button
                            text="EVALUATE CAR PRICE"
                            onClick={() => onSubmit(evaluateCarPriceClicked)} />
                    </div>
                </div>
                <div styleName={"styles.contentWrapper"} dangerouslySetInnerHTML={{ __html: secondaryContent }} />
                <div styleName={"styles.whyCars24Container"}>
                    <LazyComponent>
                        <WhyCars faqs={faqList || faqs} setFaqs={setFaqs} slug={"Used-car-valuation"}/>
                    </LazyComponent>
                </div>
            </div>
            { testimonialList && !!testimonialList.length &&
                <div styleName={"styles.testimonialWrapper"}>
                    <LazyComponent>
                        <TestimonialCarouselRevamp currentPage={"used-car-valuation"} showImg={false} minLength={NUMBER.ONE} showDate={false}/>
                    </LazyComponent>
                </div>
            }
            {
                topCIties && !!topCIties.length &&
                    <div styleName={"styles.sellCarListWrapper"}>
                        <ErrorHandler>
                            <SellUsedCarCityList cityList={topCIties} callBack={handleCityListGa} />
                        </ErrorHandler>
                    </div>
            }
            <div styleName={"styles.faq"}>
                <PilotFaq heading={"Frequently asked questions"} faqs={faqs} columns={1} attachSchema={true}/>
                <a styleName={"styles.allfaq"} href="/faq/" target="_blank">view all faqs</a>
            </div>
            <LazyComponent>
                <SimplestWay onClick={() => onSubmit(evaluateCarPriceBottomClicked)} subText={"evaluate"} ctaText={"EVALUATE CAR PRICE"}/>
            </LazyComponent>
            <BreadcrumbList
                homeText={"Home"}
                onClickCallback={onBreadcrumbClick}
                list={computeBreadcrumbList()}
                isRevamped = {true}
                attachSchema={true}
            />
        </div>
    );
};

UsedCarValuation.propTypes = {
    genericContent: PropTypes.object,
    data: PropTypes.object,
    mainHeading: PropTypes.string,
    subHeading: PropTypes.string,
    setIsSSRConnect: PropTypes.func,
    seo: PropTypes.object,
    isSSR: PropTypes.bool,
    fetchDataConnect: PropTypes.func,
    list: PropTypes.array,
    fetchFAQConnect: PropTypes.func,
    faqList: PropTypes.array
};

export default UsedCarValuation;
